// Learn: how to secure access in the age of AI, for organisations and for individuals, and the
// questions to ask a vendor. Every guide has an anchor (/learn#keys) the articles link to.
import { Buildings, CaretDown, Check, User } from "@phosphor-icons/react";
import { Link } from "react-router";

import { FOR_INDIVIDUALS, FOR_ORGANISATIONS, type Guide, VENDOR_QUESTIONS } from "../content/learn";
import { useTitle } from "../ui/hooks";
import { PageHero, SectionHead } from "../ui/Section";

function GuideCard({ guide, n }: { guide: Guide; n: number }) {
  return (
    <details id={guide.id} className="card group scroll-mt-24 open:border-ink" open={n === 1}>
      <summary className="flex cursor-pointer list-none items-start gap-4 p-5 sm:p-6">
        <span className="font-mono text-sm text-signal">{String(n).padStart(2, "0")}</span>
        <span className="flex-1">
          <span className="block font-display text-xl font-bold leading-tight tracking-tight">{guide.title}</span>
          <span className="mt-1.5 block text-[15px] text-ink2">{guide.why}</span>
        </span>
        <CaretDown weight="bold" className="mt-1 h-4 w-4 shrink-0 text-ink3 transition-transform group-open:rotate-180" aria-hidden />
      </summary>
      <ul className="space-y-2.5 border-t border-line px-5 pb-6 pt-4 sm:ps-14">
        {guide.steps.map((s) => (
          <li key={s} className="flex gap-2.5 text-[15px]">
            <Check weight="bold" className="mt-1 h-4 w-4 shrink-0 text-moss" aria-hidden /> {s}
          </li>
        ))}
      </ul>
    </details>
  );
}

export function Learn() {
  useTitle("Secure your access");
  return (
    <>
      <PageHero
        kicker="§ Learn"
        title={<>Secure your access in the age of AI.</>}
        aside={
          <nav aria-label="On this page" className="card p-5">
            <p className="kicker">On this page</p>
            <ul className="mt-3 space-y-2 text-[15px] font-semibold">
              <li>
                <a href="#organisations" className="hover:text-signal">For organisations · {FOR_ORGANISATIONS.length} guides</a>
              </li>
              <li>
                <a href="#individuals" className="hover:text-signal">For individuals · {FOR_INDIVIDUALS.length} guides</a>
              </li>
              <li>
                <a href="#vendors" className="hover:text-signal">Questions for your AI vendor</a>
              </li>
            </ul>
          </nav>
        }
      >
        Agents now read our mail, write our code and act in our names. These guides are what we wish every team and every person knew before clicking Allow. No product required.
      </PageHero>
      <section id="organisations" className="wrap scroll-mt-20 py-16">
        <SectionHead kicker="§ For organisations" title={<span className="inline-flex items-center gap-3"><Buildings weight="duotone" className="h-9 w-9" aria-hidden />Nine habits of a safe AI rollout.</span>} />
        <div className="mt-8 grid gap-3 lg:grid-cols-2">
          {FOR_ORGANISATIONS.map((g, i) => (
            <GuideCard key={g.id} guide={g} n={i + 1} />
          ))}
        </div>
      </section>
      <section id="individuals" className="scroll-mt-20 border-y border-line bg-paper2/50">
        <div className="wrap py-16">
          <SectionHead kicker="§ For individuals" title={<span className="inline-flex items-center gap-3"><User weight="duotone" className="h-9 w-9" aria-hidden />Five things to do this week.</span>} />
          <div className="mt-8 grid gap-3 lg:grid-cols-2">
            {FOR_INDIVIDUALS.map((g, i) => (
              <GuideCard key={g.id} guide={g} n={i + 1} />
            ))}
          </div>
        </div>
      </section>
      <section id="vendors" className="wrap scroll-mt-20 py-16">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.3fr]">
          <SectionHead kicker="§ Ask your vendor" title="Eight questions before you sign.">
            Print them, paste them into the procurement form, ask them in the demo. A vendor that can't answer them is telling you something.
          </SectionHead>
          <ol className="card divide-y divide-line">
            {VENDOR_QUESTIONS.map((q, i) => (
              <li key={q} className="flex gap-4 px-5 py-4 font-serif text-[1.1rem] text-ink">
                <span className="font-mono text-sm text-signal">{String(i + 1).padStart(2, "0")}</span> {q}
              </li>
            ))}
          </ol>
        </div>
        <p className="mt-10 text-[15.5px] text-ink2">
          Going deeper: read the series <Link to="/blog/i-am-inevitable" className="link">Within Our Control</Link>, or put a number on it with the <Link to="/tools" className="link">free tools</Link>.
        </p>
      </section>
    </>
  );
}
